"use client";

/**
 * Short two-tone chime for arriving notifications. Uses the Web Audio API so no
 * asset is required and it works on desktop and mobile while the app is open.
 */
let context: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!context) {
    try { context = new Ctor(); } catch { context = null; }
  }
  return context;
}

/** Called from the first user gesture so later chimes are allowed to play. */
export function primeNotificationSound() {
  const ctx = audioContext();
  if (ctx && ctx.state === "suspended") void ctx.resume().catch(() => undefined);
}

export function playNotificationChime() {
  const ctx = audioContext();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    const start = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.45);
    gain.connect(ctx.destination);
    ([["sine", 880], ["sine", 1174.66]] as Array<[OscillatorType, number]>).forEach(([type, frequency], index) => {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = frequency;
      osc.connect(gain);
      const offset = start + index * 0.16;
      osc.start(offset);
      osc.stop(offset + 0.2);
    });
  } catch {
    // Sound is a nicety; never let it break the notification flow.
  }
}

/** Shows an OS notification when the tab is in the background (if permitted). */
export function showSystemNotification(title: string, body: string, url?: string) {
  if (typeof window === "undefined" || typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.ready
        .then((registration) => registration.showNotification(title, { body, icon: "/window.svg", data: { url } }))
        .catch(() => undefined);
      return;
    }
    const notification = new Notification(title, { body, icon: "/window.svg" });
    notification.onclick = () => {
      window.focus();
      if (url) window.location.href = url;
    };
  } catch {
    // Ignore browsers that block direct Notification construction.
  }
}
